import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import { useCallback, useEffect, useMemo, useState } from "react";
import { del, get } from "../../../../utils/helpers/api";
import { useNavigate } from "react-router-dom";
import IconButton from "../../../../components/Buttons/IconButton";
import { IoCreateOutline } from "react-icons/io5";
import { BsTrash3 } from "react-icons/bs";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import DefaultButton from "../../../../components/Buttons/Default";
import DefaultPagination from "../../../../components/Pagination";
import CreateWarehouseModal from "./Modals/create";
import ConfirmationModal from "../../../../components/Modals/Confirmation";
import { useTranslation } from "react-i18next";
import AgGridTable from "../../../../components/Tables/AgGrid";

function ActionsCellRenderer({ data, context }) {
  // The in-transit location is created and consumed by stock transfers, so it is
  // not something a user should be able to rename away or delete.
  if (data.type === 'in_transit') {
    return null;
  }

  return (
    <div className="d-flex align-items-center gap-2 h-100">
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setWarehouseToEdit(data);
          context.setAddWarehouseModalOpen(true);
        }}
        size="sm" outline color="secondary"
      >
        <IoCreateOutline />
      </IconButton>
      <IconButton
        onClick={(e) => {
          e.stopPropagation();
          context.setWarehouseToDelete(data.id);
        }}
        size="sm" outline color="danger"
      >
        <BsTrash3 />
      </IconButton>
    </div>
  );
}

function WarehousesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rowData, setRowData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [addWarehouseModalOpen, setAddWarehouseModalOpen] = useState(false);
  const [warehouseToEdit, setWarehouseToEdit] = useState(null);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);

  const getWarehouses = useCallback(async () => {
    setLoading(true);
    const data = await get(`/inventory/warehouses?page=${currentPage}&page_size=${pageSize}`);
    if (data?.success) {
      setRowData(data.warehouses.data);
      setTotalPages(data.warehouses.last_page);
      setTotalRecords(data.warehouses.total);
    }
    setLoading(false);
  }, [currentPage, pageSize]);

  useEffect(() => {
    getWarehouses();
  }, [getWarehouses]);

  const deleteWarehouse = async () => {
    setLoading(true);
    const data = await del(`/inventory/warehouses/delete/${warehouseToDelete}`);
    if (data?.success) {
      getWarehouses();
    }
    setWarehouseToDelete(null);
    setLoading(false);
  };

  const columnDefs = useMemo(() => [
    { field: 'code', headerName: t('common.code'), flex: 1, sortable: true },
    { field: 'name', headerName: t('common.name'), flex: 2, sortable: true },
    {
      field: 'branch.name',
      headerName: t('branches.branch'),
      flex: 1,
      sortable: true,
      valueGetter: (params) => params.data?.branch?.name ?? '-'
    },
    {
      field: 'type',
      headerName: t('common.type'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => t(`warehouses.types.${params.value}`)
    },
    {
      field: 'is_default',
      headerName: t('warehouses.isDefault'),
      flex: 1,
      sortable: true,
      cellRenderer: (params) => params.value ? t('common.yes') : t('common.no')
    },
    { headerName: t('common.actions'), minWidth: 100, flex: 1, sortable: false, resizable: false, cellRenderer: ActionsCellRenderer }
  ], [t]);

  const defaultColDef = useMemo(() => ({ resizable: true }), []);
  const gridContext = useMemo(
    () => ({ navigate, setWarehouseToEdit, setAddWarehouseModalOpen, setWarehouseToDelete }),
    [navigate]
  );

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('warehouses.title')} onReload={getWarehouses} dataTotal={totalRecords}>
          <DefaultButton
            color="primary"
            size="sm"
            onClick={() => { setAddWarehouseModalOpen(true); setWarehouseToEdit(null); }}
          >
            {t('warehouses.newWarehouse')}
          </DefaultButton>
        </PageSubHeader>

        <div className="subheader-page-content">
          <AgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            loading={loading}
            context={gridContext}
          />

          <DefaultPagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </div>
      </div>

      <CreateWarehouseModal
        isOpen={addWarehouseModalOpen}
        toggle={() => setAddWarehouseModalOpen(false)}
        refreshWarehouses={getWarehouses}
        editData={warehouseToEdit}
      />

      <ConfirmationModal
        isOpen={warehouseToDelete !== null}
        title={t('warehouses.deleteWarehouse')}
        message={t('warehouses.deleteWarehouseConfirm')}
        onConfirm={deleteWarehouse}
        toggle={() => setWarehouseToDelete(null)}
      />
    </SidebarLayout>
  );
}

export default WarehousesPage;
